import { memo, ReactElement, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fireEvent } from 'utils/generic/event-bus';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { BetslipRootContainer } from 'newelements/_Betslip/components/containers/RootContainer';
import { BetslipTabs } from 'newelements/_Betslip/components/shared/BetslipTabs';
import { QuickBet } from 'newelements/_Betslip/components/shared/Features/QuickBet';
import { BetslipTypeBasedLayout } from 'newelements/_Betslip/layouts';
import { RootManager } from 'newelements/_Betslip/managers';
import { EBetslipConfigsDispatchActions } from 'newelements/_Betslip/types/configs';
import { useBetSlipSubscription } from 'hooks/useBetSlipSubscription';
import { getCounterOffersUnderReviewModal } from 'store/selectors/betslip';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const { CounterOfferUnderReviewModal } = lazy(
  () =>
    import(
      /* webpackChunkName: "counter-offer-under-review-modal" */ './components/CounterOfferUnderReviewModal'
    )
);

type TBetslipComponentProps = {
  isFloating?: boolean;
  componentRid?: string;
  configs?: { betslipFixedHeight: boolean };
  jackpotForSport?: boolean;
  detectTopHeight?: number;
};

const betslipComponentDefaultProps = {
  isFloating: false
} as TBetslipComponentProps;

const _BetslipComponent = ({
  isFloating,
  configs,
  detectTopHeight
}: TBetslipComponentProps = betslipComponentDefaultProps): ReactElement => {
  // create SubscribedEventsManager
  useBetSlipSubscription();
  const counterOffersUnderReviewModal = useSelector(
    getCounterOffersUnderReviewModal
  );

  // @Todo need to change useEffect into LayoutEffect after fixing issue of multiple component calling before mount
  useEffect(() => {
    fireEvent({
      key: EBetslipConfigsDispatchActions.SET_STATIC_CONFIGS,
      // @Todo change "isFloating" prop name into "floatingMode"
      payload: { ...configs, floatingMode: !!isFloating }
    });
  }, []);

  // @TODO create separate component for betslip switchers ang group all there
  if (SpringConfigs.MOCKED_DATA && isMobile()) {
    return (
      <>
        <RootManager />
        <BetslipTabs isFloating={isFloating}/>;
      </>
    );
  }

  return (
    <BetslipRootContainer
      isHeightFixed={
        configs?.betslipFixedHeight
          ? Boolean(+configs.betslipFixedHeight)
          : false
      }
      detectTopHeight={detectTopHeight}
    >
      <RootManager />
      <BetslipTabs isFloating={isFloating}/>

      <QuickBet />

      <BetslipTypeBasedLayout />
      <Suspense>
        {counterOffersUnderReviewModal && <CounterOfferUnderReviewModal />}
      </Suspense>
    </BetslipRootContainer>
  );
};

export const BetslipComponent = memo(_BetslipComponent);
